import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { ThemeSelect } from "@/components/theme-toggle";
import { NavStyleToggle } from "@/components/nav-style-toggle";
import { SkHeader, SkPanel } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import type { SettingsPage } from "@shared/pages/settings";
import { PasswordForm } from "@/features/settings/password-form";
import { ProfileForm } from "@/features/settings/profile-form";
import { ShopifyConnection } from "@/features/settings/shopify-connection";
import { ProductAliases } from "@/features/settings/product-aliases";
import { ShopperTracking } from "@/features/settings/shopper-tracking";
import { useNavStyle } from "@/lib/nav-style";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/settings")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<SettingsPage>("settings", deps));
  },
  component: SettingsRoute,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 font-heading text-sm font-bold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Connection({ name, detail, connected }: { name: string; detail: string; connected: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-foreground">{name}</h3>
        <p className="mt-0.5 text-sm text-muted">{detail}</p>
      </div>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
          connected ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-surface-2 text-muted"
        }`}
      >
        {connected ? "Connected" : "Not connected"}
      </span>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="page-shell-narrow">
      <SkHeader withAction={false} />
      <SkPanel bodyClassName="h-40" />
      <SkPanel className="mt-6" bodyClassName="h-56" />
    </div>
  );
}

function SettingsRoute() {
  useTitle("Settings");
  const search = Route.useSearch();
  return (
    <PageQuery<SettingsPage> name="settings" search={search} skeleton={<Skeleton />}>
      {(page) => <Settings page={page} />}
    </PageQuery>
  );
}

// Everyone gets their profile (name, title, password) and preferences. Where
// the data comes from (Shopify, the hub, the shopper pixel, product matches)
// is for admins: nobody else can act on it. Team and access live under /admin.
function Settings({ page }: { page: SettingsPage }) {
  const { profile, hub, isAdmin } = page;
  const navStyle = useNavStyle();

  return (
    <div className="page-shell-narrow space-y-8">
      <PageHeader title="Settings" />

      <Section title="Profile">
        <div className="divide-y divide-border rounded-xl border border-border bg-surface">
          <div className="p-5">
            <ProfileForm email={profile.email ?? ""} fullName={profile.fullName ?? ""} jobTitle={profile.jobTitle ?? ""} />
          </div>
          <div className="p-5">
            <PasswordForm />
          </div>
        </div>
      </Section>

      <Section title="Preferences">
        <div className="grid gap-4 rounded-xl border border-border bg-surface p-5 @xl:grid-cols-2">
          <div className="hidden md:block">
            <span id="menu-style-label" className="mb-1.5 block text-xs font-medium text-muted">
              Menu style
            </span>
            <NavStyleToggle value={navStyle} variant="settings" />
          </div>
          <div>
            <span id="theme-label" className="mb-1.5 block text-xs font-medium text-muted">
              Theme
            </span>
            <ThemeSelect />
          </div>
        </div>
      </Section>

      {isAdmin && page.shopify && page.tracking && (
        <Section title="Connections">
          <div className="space-y-3">
            <ShopifyConnection data={page.shopify} canSync isAdmin />
            <div className="divide-y divide-border rounded-xl border border-border bg-surface">
              <Connection
                name="Google Ads"
                detail={hub ? "Served by the Digital King hub." : "Comes through the Digital King hub, which is not linked to this dashboard yet."}
                connected={hub}
              />
              <Connection
                name="Google Analytics"
                detail={hub ? "Served by the Digital King hub." : "Comes through the Digital King hub, which is not linked to this dashboard yet."}
                connected={hub}
              />
            </div>
            <ShopperTracking data={page.tracking} isAdmin />
            {page.aliases && <ProductAliases data={page.aliases} />}
          </div>
        </Section>
      )}
    </div>
  );
}
